import React from 'react';
import { useHistory } from 'react-router-dom';
import { dateSpliter } from '../../../../helpers/dates-hanlder';
import { ReactComponent as ChevronNextIcon } from '../../../../assets/icons/chevron-next.svg';
import {
  StatusContainer,
  StatusBadge,
  StatusIcon,
  StatusContent,
  StatusMainText,
  StatusSubText,
  ChangeButton,
  ArrowIcon
} from './styles';

interface DoorStatusBadgeProps {
  group: any;
}

const DoorStatusBadge: React.FC<DoorStatusBadgeProps> = ({ group }) => {
  const history = useHistory();

  if (!group) return null;

  const isOpen = group.subscriptions_status === 'open';
  const hasOpeningScheduled = group.subscriptions_opening_date && new Date(group.subscriptions_opening_date) > new Date();
  const hasClosingScheduled = group.subscriptions_closing_date && new Date(group.subscriptions_closing_date) > new Date();

  const formatScheduledDate = (date: string) => {
    try {
      const { day, month, year, hours, minutes } = dateSpliter(date);
      return `${day.number} ${month.name.short} ${year}, ${hours}:${minutes}`;
    } catch (error) {
      return '';
    }
  };

  const handleChange = () => {
    const currentPath = history.location.pathname;
    const targetPath = `/dashboard/${group.group_type}/edit/${group.id}`;
    const hash = '#door-programming';

    // Si ya estamos en la URL de edición, solo agregamos el hash y hacemos scroll
    if (currentPath === targetPath) {
      window.location.hash = hash;
      // En desktop, el scroll está en #dashboard-page-content
      setTimeout(() => {
        const pageContent = document.getElementById('dashboard-page-content');
        const element = document.getElementById('door-programming');
        if (pageContent && element) {
          const elementRect = element.getBoundingClientRect();
          const pageContentRect = pageContent.getBoundingClientRect();
          const scrollTop = pageContent.scrollTop;
          const targetPosition = elementRect.top - pageContentRect.top + scrollTop - 120;
          pageContent.scrollTo({
            top: Math.max(0, targetPosition),
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // Si no estamos en la URL, navegamos con el hash
      history.push(`${targetPath}${hash}`);
    }
  };

  // Determinar qué variante mostrar
  const getVariant = () => {
    if (!isOpen && !hasOpeningScheduled) {
      // Inscripciones cerradas sin fecha de apertura
      return 'closed';
    }
    if (!isOpen && hasOpeningScheduled) {
      // Inscripciones cerradas con apertura programada
      return 'scheduled-to-open';
    }
    if (isOpen && hasClosingScheduled) {
      // Inscripciones abiertas con cierre programado
      return 'scheduled-to-close';
    }
    // Inscripciones abiertas sin cierre programado
    return 'open';
  };

  const variant = getVariant();

  const getLockIcon = () => {
    const isLocked = variant === 'closed' || variant === 'scheduled-to-open';
    const color = variant === 'open' ? '#2E7D32' :
      variant === 'scheduled-to-close' ? '#4285F4' :
        variant === 'scheduled-to-open' ? '#F57C00' : '#424242';

    if (isLocked) {
      // Candado cerrado
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="11" width="14" height="10" rx="2" stroke={color} strokeWidth="2" />
          <path d="M8 11V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V11" stroke={color} strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="16" r="1" fill={color} />
        </svg>
      );
    } else {
      // Candado abierto
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="11" width="14" height="10" rx="2" stroke={color} strokeWidth="2" />
          <path d="M8 11V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V9" stroke={color} strokeWidth="2" strokeLinecap="round" />
          <path d="M12 15V17" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }
  };

  const getContent = () => {
    switch (variant) {
      case 'closed':
        return {
          mainText: 'Inscripciones cerradas',
          subText: null
        };
      case 'scheduled-to-open':
        return {
          mainText: 'Abrirán el',
          subText: formatScheduledDate(group.subscriptions_opening_date)
        };
      case 'scheduled-to-close':
        return {
          mainText: 'Cerrarán el',
          subText: formatScheduledDate(group.subscriptions_closing_date)
        };
      case 'open':
      default:
        return {
          mainText: 'Inscripciones abiertas',
          subText: null
        };
    }
  };

  const content = getContent();

  return (
    <StatusContainer variant={variant}>
      <StatusBadge>
        <StatusIcon>{getLockIcon()}</StatusIcon>
        <StatusContent>
          <StatusMainText variant={variant}>{content.mainText}</StatusMainText>
          {content.subText && (
            <StatusSubText variant={variant}>{content.subText}</StatusSubText>
          )}
        </StatusContent>
      </StatusBadge>

      <ChangeButton variant={variant} type="button" onClick={handleChange}>
        Cambiar
        <ArrowIcon>
          <ChevronNextIcon />
        </ArrowIcon>
      </ChangeButton>
    </StatusContainer>
  );
};

export default DoorStatusBadge;

